import React, { useEffect, useRef, useState } from "react";
import { useNotifications } from "../contexts/NotificationContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, AlertTriangle, ClipboardList } from "lucide-react";
import { Lang, modelOf, notificationText, timeAgoText } from "../utils/notificationText";
import { recordRoute } from "../utils/recordRoutes";

// The latest notifications, read or not; the list closes when the pointer
// leaves it (after a short grace), on a click outside or Escape.
const LATEST = 6;
const LEAVE_GRACE_MS = 400;

export const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, markAsRead } = useNotifications();
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const lang = (["en", "am", "or"].includes(i18n.language?.slice(0, 2))
    ? i18n.language.slice(0, 2)
    : "en") as Lang;
  const boxRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) return;
    const outside = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === "Escape" && setIsOpen(false);
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      window.clearTimeout(timer.current);
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [isOpen]);

  const latest = [...notifications]
    .sort(
      (a: any, b: any) =>
        new Date(b.timestamp || b.created_at).getTime() - new Date(a.timestamp || a.created_at).getTime(),
    )
    .slice(0, LATEST);

  const getNotificationIcon = (type?: string) => {
    switch (type) {
      case "case_assignment":
        return <ClipboardList className="w-4 h-4" />;
      case "new_message":
        return <MessageSquare className="w-4 h-4" />;
      case "system_alert":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  const typeColors: Record<string, { 
    bg: string; 
    iconBg: string; 
    border: string; 
    text: string; 
    iconColor: string; 
    dot: string;
  }> = {
    case_assignment: {
      bg: "#eff6ff",
      iconBg: "#dbeafe",
      border: "#3b82f6",
      text: "#1e40af",
      iconColor: "#1d4ed8",
      dot: "#3b82f6"
    },
    new_message: {
      bg: "#f0fdf4",
      iconBg: "#dcfce7",
      border: "#22c55e",
      text: "#166534",
      iconColor: "#16a34a",
      dot: "#22c55e"
    },
    system_alert: {
      bg: "#fef2f2",
      iconBg: "#fee2e2",
      border: "#ef4444",
      text: "#991b1b",
      iconColor: "#dc2626",
      dot: "#ef4444"
    },
    default: {
      bg: "#fffbeb",
      iconBg: "#fef3c7",
      border: "#f59e0b",
      text: "#92400e",
      iconColor: "#d97706",
      dot: "#f59e0b"
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    setIsOpen(false);

    // Open the record's page (the backend sends teams as their request).
    const route = recordRoute(modelOf(notification), notification.object_id);
    if (route) navigate(route);
  };

  const getColors = (type?: string) => {
    return typeColors[type || "default"] || typeColors.default;
  };

  return (
    <div
      ref={boxRef}
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => window.clearTimeout(timer.current)}
      onMouseLeave={() => {
        if (!isOpen) return;
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setIsOpen(false), LEAVE_GRACE_MS);
      }}
    >
      {/* Notification Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: "relative",
          background: isOpen ? "#e3f2fd" : "transparent",
          border: "none",
          cursor: "pointer",
          padding: "8px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Bell
          style={{
            width: "20px",
            height: "20px",
            color: "#374151",
          }}
        />
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: "-2px",
              right: "-2px",
              backgroundColor: "#dc2626",
              color: "white",
              borderRadius: "50%",
              width: "18px",
              height: "18px",
              fontSize: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              lineHeight: 1,
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Notifications */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            width: "350px",
            maxHeight: "400px",
            overflowY: "auto",
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              padding: "15px",
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <strong>{t("notifications")}</strong>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "16px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>

          <div>
            {notifications.length === 0 ? (
              <div
                style={{ padding: "20px", textAlign: "center", color: "#666" }}
              >
                {t("noNotifications")}
              </div>
            ) : (
              latest.map((notification: any) => {
                const colors = getColors(notification.type);
                const text = notificationText(notification, lang, t);
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    style={{
                      display: "flex",
                      gap: "10px",
                      padding: "12px 15px",
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor: notification.is_read ? "white" : colors.bg,
                      borderLeft: `4px solid ${notification.is_read ? "transparent" : colors.border}`,
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                      alignItems: "flex-start",
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                      if (!notification.is_read) {
                        e.currentTarget.style.backgroundColor = colors.bg;
                      }
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                      if (!notification.is_read) {
                        e.currentTarget.style.backgroundColor = colors.bg;
                      }
                    }}
                  >
                    <div style={{ flexShrink: 0, marginTop: "2px" }}>
                      <div
                        style={{
                          padding: "4px",
                          borderRadius: "4px",
                          backgroundColor: notification.is_read ? "#f3f4f6" : colors.iconBg,
                          color: notification.is_read ? "#9ca3af" : colors.iconColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: notification.is_read ? "normal" : "600",
                          color: notification.is_read ? "#666" : colors.text,
                          fontSize: "13px",
                          lineHeight: "1.4",
                          marginBottom: "4px",
                        }}
                      >
                        {text.title && <div>{text.title}</div>}
                        <div style={{ fontWeight: "normal" }}>{text.message}</div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "11px",
                          color: "#888",
                        }}
                      >
                        <span>
                          {timeAgoText(notification.timestamp || notification.created_at, lang)}
                        </span>
                        {!notification.is_read && (
                          <span
                            style={{
                              display: "inline-block",
                              width: "8px",
                              height: "8px",
                              backgroundColor: colors.dot,
                              borderRadius: "50%",
                            }}
                          ></span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default NotificationBell;